/**
 * Every tunable number in Isidora lives here, grouped by the thing it shapes.
 * Layers read `ctx.settings`; nothing reads a magic number from its own file.
 *
 * `quality` is resolved at boot from the device, then merged over the base so
 * a weak GPU gets fewer shells rather than a different program.
 */

export const BASE = {
  // ── The stair ────────────────────────────────────────────────────────────
  // One metre is one unit. The shaft is 232 m deep and 27 m across, which is
  // cathedral scale; the stair itself stays at the size of a stair.
  stair: {
    turns: 11, // complete revolutions from the top landing to the square
    height: 232, // metres of descent
    innerRadius: 7.6, // the edge of the open well
    outerRadius: 13.2, // where the tread meets the shaft wall
    wallRadius: 13.75, // the inside face of the building
    stepsPerTurn: 72,
    treadThickness: 0.26,
    fasciaDepth: 1.5, // the spiral ribbon seen from across the well
    railHeight: 1.06,
    railRadius: 0.058,
    balusterRadius: 0.042,
    balusterEvery: 3, // one baluster per N steps
  },

  // ── The traveller ────────────────────────────────────────────────────────
  camera: {
    fov: 56,
    near: 0.08,
    far: 900,
    eyeHeight: 1.72,
    walkRadiusBias: 0.54, // 0 = hug the well, 1 = hug the wall

    // Where the traveller is looking when they are not looking anywhere.
    // Not along the tangent — that stares into the wall nine metres ahead.
    // Across the well and a little down, which is the only direction from
    // which a spiral stair looks like a spiral stair: you see the turn you
    // are about to take, the turn below it, and the turn above.
    aimTurn: 2.55, // radians around the axis from where you stand
    aimRadius: 4.8, // how far from the axis the aim point sits
    aimDrop: 3.3, // metres below the eye — enough to see the turn below,
    // little enough that the handrail stays out of the middle of the frame

    yawRange: 0.68, // radians of pointer look
    pitchRange: 0.46,
    smoothing: 2.4, // critically-damped follow, higher = tighter
    swayAmount: 0.03,
  },

  // ── Movement ─────────────────────────────────────────────────────────────
  journey: {
    wheelScale: 0.00019,
    dragScale: 0.00085,
    keyScale: 0.07, // journey units per second
    autoDrift: 0.0036, // the stair pulls you down even at rest
    inertia: 0.935,
    damping: 3.1,
  },

  // ── Encrustation ─────────────────────────────────────────────────────────
  shells: {
    count: 19000,
    minScale: 0.085,
    maxScale: 0.5,
    chunks: 26, // stretches of stair that are culled as one
    // Shells do not fall like snow; they settle in colonies. Each cluster is
    // one colony, and `clusterSpread` is how far it creeps.
    clusterSize: 22,
    clusterSpread: 0.0016, // in journey units — about a fifth of a step
    // How the encrustation divides itself between the surfaces a walker can
    // actually see. Weights are normalised, so they need not sum to one.
    zones: {
      nosing: 0.26, // the front edge of every tread, underfoot
      balustrade: 0.22, // arm's length
      fasciaOut: 0.16, // the face of the ribbon you walk beside
      fasciaIn: 0.14, // the face you see across the well
      wall: 0.12,
      soffit: 0.1, // overhead, when you look up the shaft
    },
  },

  // ── Instruments ──────────────────────────────────────────────────────────
  telescopes: { count: 13, firstAt: 0.035, lastAt: 0.92 },
  violins: { count: 11, firstAt: 0.08, lastAt: 0.95 },

  // ── The square at the foot ───────────────────────────────────────────────
  elders: { count: 23, appearsAt: 0.8 },

  // ── The dreamed city, seen down the well and through the glass ───────────
  // The shaft stands in the middle of Isidora, not on a plain outside it, so
  // the nearest roofs begin just past the wall. Anything further than about
  // 150 m is lost in the dusk anyway.
  dreamCity: { towers: 200, innerRadius: 38, radius: 200, minHeight: 14, maxHeight: 128 },

  // ── The shaft that holds the stair ───────────────────────────────────────
  shaft: { bays: 16, storeys: 22 },

  // ── Atmosphere ───────────────────────────────────────────────────────────
  atmosphere: {
    fogDensity: 0.0092,
    fogDensityNight: 0.0124,
    motes: 2600,
    stars: 1800,
  },

  // ── Desire → memory ──────────────────────────────────────────────────────
  desire: {
    dwellToRemember: 1.15, // seconds of attention before a thing turns
    decay: 0.55, // attention bleeds away this fast when you look elsewhere
    wakeCapacity: 900,
    wakeDrift: 0.42,
  },

  // ── Post ─────────────────────────────────────────────────────────────────
  post: {
    // The bloom is for light sources only — lamps, lit openings, the motes.
    // A low threshold turns the whole shaft into fog.
    bloomStrength: 0.44,
    bloomRadius: 0.62,
    bloomThreshold: 0.62,
    grain: 0.05,
    vignette: 0.82,
    // Old glass, not a broken lens: enough to colour the extreme corners.
    aberration: 0.0007,
    exposure: 1.0,
  },

  // ── Sound ────────────────────────────────────────────────────────────────
  audio: {
    master: 0.52,
    violinRadius: 18,
    reverbSeconds: 5.5,
    reverbDecay: 2.6,
  },

  debug: { inspector: false },
};

const TIERS = {
  high: {},
  medium: {
    shells: { count: 7000 },
    atmosphere: { motes: 1500, stars: 1200 },
    dreamCity: { towers: 130 },
    post: { bloomRadius: 0.6 },
  },
  low: {
    stair: { stepsPerTurn: 48, balusterEvery: 4 },
    shells: { count: 3200 },
    atmosphere: { motes: 700, stars: 700 },
    dreamCity: { towers: 80 },
    shaft: { bays: 12, storeys: 18 },
    telescopes: { count: 6 },
    violins: { count: 5 },
    post: { bloomStrength: 0.5, bloomRadius: 0.5 },
  },
};

function deepMerge(base, patch) {
  const out = Array.isArray(base) ? base.slice() : { ...base };
  for (const [k, v] of Object.entries(patch ?? {})) {
    out[k] = v && typeof v === 'object' && !Array.isArray(v) ? deepMerge(base[k] ?? {}, v) : v;
  }
  return out;
}

export function detectTier() {
  if (typeof navigator === 'undefined') return 'high';
  const params = new URLSearchParams(globalThis.location?.search ?? '');
  const forced = params.get('quality');
  if (forced && TIERS[forced]) return forced;

  const mem = navigator.deviceMemory ?? 8;
  const cores = navigator.hardwareConcurrency ?? 8;
  const coarse = globalThis.matchMedia?.('(pointer: coarse)')?.matches ?? false;
  const dpr = globalThis.devicePixelRatio ?? 1;
  if (coarse || mem <= 4 || cores <= 4) return 'low';
  if (mem <= 8 && dpr > 2) return 'medium';
  return 'high';
}

export function resolveSettings(tier = detectTier()) {
  const settings = deepMerge(BASE, TIERS[tier] ?? {});
  settings.tier = tier;
  const params = new URLSearchParams(globalThis.location?.search ?? '');
  if (params.has('inspect')) settings.debug.inspector = true;
  return settings;
}
